import { ApolloError } from '@apollo/client';
import { portalPaths } from '@provision/portal-routes';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import {
  GET_ACTIVATION_FLOW_CONFIG_QUERY,
  useBrands,
  useCreateInitialMembershipsConfig,
  useMembershipsActivationFlowConfig,
  useMembershipsPartnerProducts,
} from 'src/graphql';
import { MembershipsConfigViewLayout } from 'src/pages/Settings/memberships/components/memberships-config-view-layout';
import { MembershipsStripeBlocker } from 'src/pages/Settings/memberships/components/memberships-stripe-blocker';
import {
  DEFAULT_PLANS_TAB_VALUES,
  PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import { composeMembershipsFormKey } from 'src/pages/Settings/memberships/utils/compose-memberships-form-key';
import { mapActivationFlowConfigToPlansTabFormValues } from 'src/pages/Settings/memberships/utils/map-activation-flow-config-to-form-values';
import { parseMembershipsConfigError } from 'src/pages/Settings/memberships/utils/parse-memberships-config-error';
import { useStripeAccountOnboardStatus } from 'src/pages/Settings/stripe/use-stripe-account-onboard-status';
import { useGlobalStore } from 'src/store';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { useNavigateRoute } from 'src/utils/routing';

export const MembershipsConfigView = (): ReactElement => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { navigate: navigateRoute } = useNavigateRoute();
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [isNewConfig, setIsNewConfig] = useState(false);
  const [refreshErrorMessage, setRefreshErrorMessage] = useState<string | null>(null);
  const [isRefreshingConfig, setIsRefreshingConfig] = useState(false);
  const [isAwaitingPartnerProducts, setIsAwaitingPartnerProducts] = useState(false);
  const { showErrorAlert } = useNotifications();
  const {
    hasAccount: hasStripeAccount,
    loading: stripeStatusLoading,
    error: stripeStatusError,
    refetch: refetchStripeStatus,
  } = useStripeAccountOnboardStatus();
  const shouldSkipMembershipsData =
    isNullish(partnerId) ||
    stripeStatusLoading ||
    Boolean(stripeStatusError) ||
    !hasStripeAccount;

  const activeConfigKey =
    !isNullish(partnerId) && selectedSegmentId ? `${partnerId}:${selectedSegmentId}` : null;
  const handleGoToStripe = useCallback(() => {
    navigateRoute(portalPaths.settings.stripe());
  }, [navigateRoute]);
  const { brands: segments } = useBrands(isNullish(partnerId) ? null : partnerId, {
    skip: shouldSkipMembershipsData,
  });

  const segmentIds = useMemo(
    () =>
      Array.from(new Set(segments.map((segment) => String(segment.brandId)).filter(Boolean))),
    [segments],
  );

  const activationFlowConfigQuery = useMembershipsActivationFlowConfig(
    {
      partnerId: isNullish(partnerId) ? null : partnerId,
      brandId: selectedSegmentId,
    },
    {
      skip: shouldSkipMembershipsData,
    },
  );
  const activationFlowCache = useCacheManager(
    GET_ACTIVATION_FLOW_CONFIG_QUERY,
    // @ts-expect-error -- fix types later
    activationFlowConfigQuery,
  );

  const partnerProductsQuery = useMembershipsPartnerProducts(
    {
      partnerId: isNullish(partnerId) ? null : partnerId,
    },
    {
      skip: shouldSkipMembershipsData,
    },
  );

  const { createInitialMembershipsConfig, loading: createConfigLoading } =
    useCreateInitialMembershipsConfig();

  const handleConfigSaved = useCallback(async () => {
    if (isNullish(partnerId) || !selectedSegmentId) {
      return;
    }

    activationFlowCache.reset({
      fieldName: 'getBundledActivationFlowConfig',
      args: {
        input: {
          partnerId: String(partnerId),
          brandId: selectedSegmentId,
        },
      },
    });

    await activationFlowConfigQuery.refetch();
  }, [activationFlowCache, activationFlowConfigQuery, partnerId, selectedSegmentId]);

  useEffect(() => {
    setRefreshErrorMessage(null);
    setIsAwaitingPartnerProducts(false);
  }, [partnerId]);

  const isInitialConfigLoading =
    activationFlowConfigQuery.loading && !activationFlowConfigQuery.config;
  const isInitialProductsLoading =
    partnerProductsQuery.loading && !partnerProductsQuery.partnerProducts.length;
  const isStripeStatusLoading = !isNullish(partnerId) && stripeStatusLoading;
  const isStripeStatusError = !isNullish(partnerId) && Boolean(stripeStatusError);
  const isLoading =
    Boolean(selectedSegmentId) &&
    (isInitialConfigLoading ||
      isInitialProductsLoading ||
      isStripeStatusLoading ||
      isAwaitingPartnerProducts ||
      createConfigLoading ||
      isRefreshingConfig);
  const isStripeBlocked =
    !isNullish(partnerId) && !stripeStatusLoading && !stripeStatusError && !hasStripeAccount;

  const data = useMemo<PlansTabFormValues | null>(() => {
    if (activationFlowConfigQuery.config) {
      if (isAwaitingPartnerProducts && !partnerProductsQuery.partnerProducts.length) {
        return null;
      }
      return mapActivationFlowConfigToPlansTabFormValues(
        activationFlowConfigQuery.config,
        partnerProductsQuery.partnerProducts,
      );
    }

    if (isNewConfig) {
      return DEFAULT_PLANS_TAB_VALUES;
    }

    return null;
  }, [
    activationFlowConfigQuery.config,
    isAwaitingPartnerProducts,
    isNewConfig,
    partnerProductsQuery.partnerProducts,
  ]);

  const formKey = useMemo(
    () => composeMembershipsFormKey(activeConfigKey, isNewConfig),
    [activeConfigKey, isNewConfig],
  );

  const refreshConfig = useCallback(
    async (segmentIdsToRefresh?: string[]) => {
      if (isNullish(partnerId)) {
        return false;
      }

      const fallbackSegmentIds = selectedSegmentId ? [selectedSegmentId] : [];
      const refreshSegmentIds = segmentIdsToRefresh?.length
        ? segmentIdsToRefresh
        : fallbackSegmentIds;

      if (!refreshSegmentIds.length) {
        return false;
      }

      const uniqueSegmentIds = Array.from(new Set(refreshSegmentIds.filter(Boolean)));

      if (!uniqueSegmentIds.length) {
        return false;
      }

      setIsRefreshingConfig(true);
      setRefreshErrorMessage(null);

      try {
        uniqueSegmentIds.forEach((segmentId) => {
          activationFlowCache.reset({
            fieldName: 'getBundledActivationFlowConfig',
            args: {
              input: {
                partnerId: String(partnerId),
                brandId: segmentId,
              },
            },
          });
        });

        await Promise.all([
          ...uniqueSegmentIds.map((segmentId) =>
            activationFlowCache.client.query({
              query: GET_ACTIVATION_FLOW_CONFIG_QUERY,
              variables: {
                input: {
                  partnerId: String(partnerId),
                  brandId: segmentId,
                },
              },
              fetchPolicy: 'network-only',
            }),
          ),
          partnerProductsQuery.refetch?.(),
        ]);

        if (selectedSegmentId && uniqueSegmentIds.includes(selectedSegmentId)) {
          await activationFlowConfigQuery.refetch();
        }

        return true;
      } catch (error) {
        console.error(error);
        setRefreshErrorMessage('Unable to refresh configuration. Please try again.');
        return false;
      } finally {
        setIsRefreshingConfig(false);
      }
    },
    [
      activationFlowCache,
      activationFlowConfigQuery,
      partnerId,
      partnerProductsQuery,
      selectedSegmentId,
    ],
  );

  const handleSegmentChange = useCallback((segmentId: string | null) => {
    setSelectedSegmentId(segmentId);
    setIsNewConfig(false);
    setRefreshErrorMessage(null);
  }, []);

  const handleCreateConfig = useCallback(async () => {
    if (isNullish(partnerId) || !selectedSegmentId) {
      return;
    }

    try {
      setIsAwaitingPartnerProducts(true);
      await createInitialMembershipsConfig({
        variables: {
          partnerId,
        },
      });
      await refreshConfig(segmentIds);
    } catch (error) {
      console.error(error);
      setIsAwaitingPartnerProducts(false);
      const errorMessage =
        error instanceof ApolloError
          ? parseMembershipsConfigError(error)
          : 'Unable to create membership configuration. Please try again.';
      showErrorAlert(errorMessage);
    }
  }, [
    createInitialMembershipsConfig,
    partnerId,
    refreshConfig,
    segmentIds,
    selectedSegmentId,
    showErrorAlert,
  ]);

  const handleRetryRefresh = useCallback(async () => {
    await refreshConfig();
  }, [refreshConfig]);

  const handleRetryStripeStatus = useCallback(() => {
    void refetchStripeStatus();
  }, [refetchStripeStatus]);

  const hasPartnerProducts = partnerProductsQuery.partnerProducts.length > 0;
  const isWaitingForPartnerProducts =
    isAwaitingPartnerProducts && !hasPartnerProducts && !partnerProductsQuery.loading;
  const showCreateConfig =
    !activationFlowConfigQuery.loading &&
    !isNullish(partnerId) &&
    Boolean(selectedSegmentId) &&
    !activationFlowConfigQuery.config;
  const isCreatingConfig =
    createConfigLoading || isRefreshingConfig || isWaitingForPartnerProducts;

  useEffect(() => {
    if (!isAwaitingPartnerProducts) {
      return;
    }

    if (hasPartnerProducts) {
      setIsAwaitingPartnerProducts(false);
    }
  }, [hasPartnerProducts, isAwaitingPartnerProducts]);

  if (isStripeStatusLoading || isStripeStatusError || isStripeBlocked) {
    return (
      <MembershipsStripeBlocker
        isLoading={isStripeStatusLoading}
        errorMessage={
          isStripeStatusError
            ? 'We could not confirm Stripe setup for this partner. Please try again.'
            : null
        }
        onGoToStripe={handleGoToStripe}
        onRetry={handleRetryStripeStatus}
      />
    );
  }

  return (
    <MembershipsConfigViewLayout
      selectedSegmentId={selectedSegmentId}
      onSegmentChange={handleSegmentChange}
      onCreateConfig={handleCreateConfig}
      isNewConfig={isNewConfig}
      isLoading={isLoading}
      error={activationFlowConfigQuery.error}
      data={data}
      activationFlowConfig={activationFlowConfigQuery.config}
      formKey={formKey}
      partnerId={partnerId}
      activationFlowConfigId={activationFlowConfigQuery.config?.id ?? null}
      onSaved={handleConfigSaved}
      showCreateConfig={showCreateConfig}
      isCreatingConfig={isCreatingConfig}
      refreshErrorMessage={refreshErrorMessage}
      onRetryRefresh={handleRetryRefresh}
    />
  );
};
